import React, { useCallback } from 'react';
import PropTypes from 'prop-types';

import { NotificationManager, SiteConfigManager } from '@atlas/core';
import { BasePopup, Button, Icon, Label } from '@atlas/ui';

import { ChatNameLiveChat } from 'components/LiveChat/LiveChatFactory';
import useLiveChat from 'components/LiveChat/useLiveChat';

import AlternativeMethodItem from './AlternativeMethodItem';

const MAX_ALTERNATIVE_METHODS = 3;

export const AlternativeMethodsPopup = ({ data: { methods, onSelectMethod, onViewAll }, id }) => {
  const isChatAvailable = SiteConfigManager.getConfig(
    'config.config.support.live_chat.mobile.is_available',
  );
  const chatType = SiteConfigManager.getConfig('config.config.support.live_chat.mobile.type');
  const { toggleIsOpened: toggleIsChatOpened } = useLiveChat();
  const isLiveChatAvailable = isChatAvailable && chatType === ChatNameLiveChat;

  const handleClose = useCallback(() => {
    NotificationManager.removeNotification(id);
  }, [id]);

  const handleSelectMethod = useCallback(
    method => {
      handleClose();
      onSelectMethod(method);
    },
    [handleClose, onSelectMethod],
  );

  const handleViewAll = useCallback(() => {
    handleClose();
    onViewAll();
  }, [handleClose, onViewAll]);

  const handleLiveChat = useCallback(() => {
    handleClose();
    toggleIsChatOpened();
  }, [handleClose, toggleIsChatOpened]);

  return (
    <BasePopup className="alternative-methods-popup">
      <button className="close-popup" onClick={handleClose} type="button">
        <Icon name="close" />
      </button>

      <div className="popup__content">
        <div className="alternative-methods-popup__error-icon">
          <Icon height={48} name="error-circle" width={48} />
        </div>
        <Label className="alternative-methods-popup__title" message="alternative_method_title" />
        <Label className="alternative-methods-popup__text" message="alternative_method_text" />

        <div className="alternative-methods-popup__list">
          {methods.slice(0, MAX_ALTERNATIVE_METHODS).map(method => (
            <AlternativeMethodItem
              key={method.id}
              method={method}
              onClick={() => handleSelectMethod(method)}
            />
          ))}
        </div>
      </div>

      <div className="popup__footer">
        <Button
          className="primary big block alternative-methods-popup__view-all-btn"
          label="view_all"
          onClick={handleViewAll}
        />
        {isLiveChatAvailable && (
          <div className="alternative-methods-popup__chat">
            <Label
              className="alternative-methods-popup__chat-text"
              message="alternative_method_help"
            />
            <button
              className="alternative-methods-popup__chat-btn"
              onClick={handleLiveChat}
              type="button"
            >
              <Icon height={13.3} name="livechat-message" width={13.3} />
              <Label message="live_chat" />
            </button>
          </div>
        )}
      </div>
    </BasePopup>
  );
};

AlternativeMethodsPopup.propTypes = {
  data: PropTypes.shape({
    methods: PropTypes.arrayOf(
      PropTypes.shape({
        desktopImageLink: PropTypes.string,
        id: PropTypes.number.isRequired,
        maxAmount: PropTypes.number,
        minAmount: PropTypes.number,
        name: PropTypes.string.isRequired,
      }),
    ).isRequired,
    onSelectMethod: PropTypes.func.isRequired,
    onViewAll: PropTypes.func.isRequired,
  }).isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default AlternativeMethodsPopup;
